import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import Entity from '../../Entity/EntityList'
import { Box, LinearProgress, Typography } from '@mui/material'
import { getApplicationSettings } from '../../../services/applications'
import { useAppSelector } from '../../../redux/hook'
import {
    ANALYTICS_TABS,
    Applications,
    PREVIEW_MODE,
    UserPermissions,
} from '../../../constants/constants'
import {
    a11yProps,
    CustomTab,
    CustomTabPanel,
    CustomTabs,
} from '../../Common/MuiTabs'
import { IAnalytcsSettings } from './interfaces'
import AnalyticsSettingsConfiguration from './Settings'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '../../../hooks/usePermissions'

const Analytics = (): ReactElement => {
    const { t } = useTranslation()
    const org = useAppSelector((state) => state.org.org)
    const orgId = org?.id ? org.id : ''
    const { hasPermission } = usePermissions()
    const hasPermissionAccess = useMemo(
        () =>
            hasPermission([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermission],
    )
    const hasWriteAccess = useMemo(
        () => hasPermission([UserPermissions.ORG_ADMIN]),
        [hasPermission],
    )
    const [settings, setSettings] = useState<IAnalytcsSettings>(
        {} as IAnalytcsSettings,
    )
    const [loadingSettings, setLoadingSettings] = useState<boolean>(true)
    const [tabIndex, setTabIndex] = useState<number>(ANALYTICS_TABS.LIST_VIEW)

    const { enable, analyticsEntityTemplateId } = settings
    const isAnalyticsConfigured =
        enable === 'true' && !!analyticsEntityTemplateId
    const updateSettings = useCallback((settings: IAnalytcsSettings) => {
        setSettings(settings)
    }, [])
    const previewMode = PREVIEW_MODE.VIEW_ANALYTICS_DASHBOARD
    useEffect(() => {
        const getSettings = async () => {
            setLoadingSettings(true)
            const response = await getApplicationSettings<IAnalytcsSettings>(
                orgId,
                Applications.analytics.id,
            )
            const settings = response?.appSettings
            updateSettings(settings || ({} as IAnalytcsSettings))
            setLoadingSettings(false)
        }
        getSettings()
    }, [orgId, updateSettings])

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue)
    }

    if (loadingSettings) {
        return <LinearProgress />
    }

    const analyticsNotConfigured = () => {
        return (
            <Box>
                <Typography>{t('notConfigured')}</Typography>
            </Box>
        )
    }

    return (
        <>
            {' '}
            <CustomTabs
                value={tabIndex}
                onChange={handleChange}
                aria-label={t('basictabs')}
                TabIndicatorProps={{
                    children: <span />,
                }}
            >
                <CustomTab
                    label={t('businessIntelligence')}
                    {...a11yProps(ANALYTICS_TABS.LIST_VIEW)}
                />
                {hasPermissionAccess && (
                    <CustomTab
                        label={t('settings')}
                        {...a11yProps(ANALYTICS_TABS.SETTINGS)}
                    />
                )}
            </CustomTabs>
            <CustomTabPanel value={tabIndex} index={ANALYTICS_TABS.LIST_VIEW}>
                {!isAnalyticsConfigured ? (
                    analyticsNotConfigured()
                ) : (
                    <Entity
                        previewMode={previewMode}
                        templateId={analyticsEntityTemplateId}
                        applicationId={Applications.analytics.id}
                    />
                )}
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={ANALYTICS_TABS.SETTINGS}>
                <AnalyticsSettingsConfiguration
                    {...settings}
                    updateSettings={updateSettings}
                    orgId={orgId}
                    hasWriteAccess={hasWriteAccess}
                />
            </CustomTabPanel>
        </>
    )
}

export default Analytics
